import React from "react";
import { Package } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { fileUrl } from "../../lib/api";
import ItemCard from "./ItemCard";
import { ITEM_TYPES } from "./constants";

export default function CategoryCard({ group, meta, onOpen }) {
  const { name, count, totalQty, anyDefect, defectCount, colors, types, sample, items, priorityRank, psa_category, psa_size } = group;
  const unit = sample?.unit || "buc";
  const typeLabel = ITEM_TYPES.find((t) => t.code === types[0])?.label || types[0] || "";
  // Etichetă principală: „Tricou M" / „Hanorac L" / „Cizme 42" — extrasă din câmpurile PSA.
  // Dacă nu e PSA, folosim doar numele (fallback la comportament vechi).
  const catCapitalized = psa_category
    ? psa_category.charAt(0).toUpperCase() + psa_category.slice(1)
    : "";
  const primaryLabel = psa_category
    ? `${catCapitalized}${psa_size ? ` ${psa_size}` : ""}`
    : name;
  const secondaryLabel = psa_category ? name : "";
  // pick a thumbnail from the first item that has photos
  const itemWithPhoto = items.find((i) => Array.isArray(i.photo_ids) && i.photo_ids.length > 0);
  const thumbUrl = itemWithPhoto ? fileUrl(itemWithPhoto.photo_ids[0]) : null;
  // Stilizare card pentru defecte — cu prioritate vizuală pentru top 3 categorii
  const isPriorityDefect = anyDefect && priorityRank <= 3;
  const cardCls = isPriorityDefect
    ? "border-2 border-red-600 ring-2 ring-red-300 animate-pulse-red-soft bg-red-50/30"
    : anyDefect
      ? "border-2 border-orange-500 ring-1 ring-orange-200 animate-pulse-orange-soft"
      : "border border-[var(--border)]";

  const cardTitle = anyDefect
    ? `${primaryLabel}${secondaryLabel ? " · " + secondaryLabel : ""} · ${count} produs${count !== 1 ? "e" : ""} · ${defectCount} DEFECT${defectCount !== 1 ? "E" : ""}`
    : `${primaryLabel}${secondaryLabel ? " · " + secondaryLabel : ""} · ${count} produs${count !== 1 ? "e" : ""} · ${totalQty} ${unit}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`text-left rounded-md p-3 flex flex-col gap-1.5 hover:border-[var(--primary)] hover:shadow-sm transition-all bg-white ${cardCls}`}
      data-testid={`category-card-${group.key}`}
      title={cardTitle}
    >
      {/* Banner prioritate critică pentru magazioner — afișat doar pentru rank 1-3 */}
      {isPriorityDefect && (
        <div className="-mx-3 -mt-3 mb-1 px-2 py-1 bg-red-600 text-white text-[9px] font-extrabold uppercase tracking-widest text-center" data-testid={`priority-defect-${priorityRank}`}>
          🔥 P{priorityRank} · DEFECT URGENT
        </div>
      )}
      <div className="flex items-start gap-2">
        <span
          className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${anyDefect ? "animate-pulse" : ""}`}
          style={{ background: anyDefect ? "var(--red-bila)" : "var(--green-valid)" }}
          title={anyDefect ? "Conține defecte" : "Funcțional"}
        />
        <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200 flex items-center justify-center">
          {thumbUrl ? (
            <img src={thumbUrl} alt={primaryLabel} className="w-full h-full object-cover" />
          ) : (
            <Package size={18} className="text-zinc-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-bold leading-tight truncate ${anyDefect ? "text-red-800" : "text-zinc-900"}`} title={primaryLabel}>
            {primaryLabel}
          </div>
          {secondaryLabel && (
            <div className="text-[11px] text-zinc-600 truncate leading-tight" title={secondaryLabel}>
              {secondaryLabel}
            </div>
          )}
          <div className="text-[10px] text-zinc-400 truncate mt-0.5">
            {typeLabel}
            {types.length > 1 ? " +" : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[11px] font-bold text-zinc-700 tabular-nums">
          {totalQty.toLocaleString("ro-RO")} {unit}
        </span>
        {anyDefect ? (
          <span className="text-[10px] font-extrabold text-red-700 bg-red-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
            {defectCount} defect{defectCount !== 1 ? "e" : ""}
          </span>
        ) : (
          <span className="text-[10px] text-zinc-500">
            {count} card{count !== 1 ? "uri" : ""}
          </span>
        )}
      </div>
      {colors.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-0.5">
          {colors.slice(0, 6).map((c) => {
            const co = meta.colors.find((x) => x.code === c);
            if (!co) return null;
            return (
              <span
                key={c}
                className="w-2.5 h-2.5 rounded-full border border-white shadow-[0_0_0_1px_rgba(0,0,0,0.06)]"
                style={{ background: co.hex }}
                title={co.label}
              />
            );
          })}
          {colors.length > 6 && (
            <span className="text-[9px] text-zinc-400">+{colors.length - 6}</span>
          )}
        </div>
      )}
    </button>
  );
}


export function CategoryGroupDialog({ group, meta, onClose, onMove, onEdit, onHistory, onRepair, onIssue, onPhotos }) {
  const { name, items, location, psa_category, psa_size } = group;
  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const unit = items[0]?.unit || "buc";
  const catCap = psa_category ? psa_category.charAt(0).toUpperCase() + psa_category.slice(1) : "";
  const primaryLabel = psa_category ? `${catCap}${psa_size ? ` ${psa_size}` : ""}` : name;
  const secondaryLabel = psa_category ? name : "";
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl" data-testid="category-group-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={18} className="text-[var(--primary)]" />
            <span className="truncate">
              {primaryLabel}
              {secondaryLabel && <span className="ml-2 text-sm font-normal text-zinc-500">· {secondaryLabel}</span>}
            </span>
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            {location} · {items.length} produs{items.length !== 1 ? "e" : ""} · {totalQty.toLocaleString("ro-RO")} {unit} total
          </div>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[70vh] overflow-y-auto">
          {items.map((it) => (
            <ItemCard
              key={it.id}
              it={it}
              meta={meta}
              onMove={() => onMove(it)}
              onEdit={() => onEdit(it)}
              onHistory={() => onHistory(it)}
              onRepair={() => onRepair(it)}
              onIssue={() => onIssue(it)}
              onPhotos={() => onPhotos(it)}
            />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
